// Service Plans API Service
import { getApiBaseUrl as getPermanentApiBaseUrl } from '../config/permanent-urls';
import type {
  ServicePlan,
  UserSubscription,
  SubscriptionUpgradeRequest,
  PlanChangeNotification,
} from '../../../shared/types/servicePlans';
import { createInsufficientTokensError, handleInsufficientTokensError } from '../utils/insufficientTokensHandler';
import { redactPII } from '../utils/debugLogging';

export interface StripePricingTableConfig {
  pricingTableId: string;
  publishableKey: string;
  source?: string;
}

const getApiBaseUrl = () => {
  return getPermanentApiBaseUrl();
};

const API_BASE_URL = getApiBaseUrl();

class ServicePlansService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
    this.token = localStorage.getItem('token');
  }

  private createHeaders(extra?: HeadersInit, includeJson: boolean = true, requireAuth: boolean = true): Headers {
    const headers = new Headers(extra);
    
    if (includeJson) {
      headers.set('Content-Type', 'application/json');
    }
    
    // Only add auth headers if authentication is required
    if (requireAuth) {
      // CRITICAL: Always read from localStorage to get the latest token (after refresh)
      // This ensures we use the refreshed token even if this.token is stale
      const tokenFromStorage = localStorage.getItem('token');
      if (tokenFromStorage) {
        this.token = tokenFromStorage; // Update cached token
        headers.set('Authorization', `Bearer ${tokenFromStorage}`);
      } else if (this.token) {
        // Fallback to cached token if localStorage is empty (shouldn't happen, but be safe)
        headers.set('Authorization', `Bearer ${this.token}`);
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth: boolean = true
  ): Promise<T> {
    let url = `${this.baseUrl}${endpoint}`;
    
    // CRITICAL: Add cache-busting to prevent stale subscription data
    // This ensures the UI always shows up-to-date token balances from the database
    const isGetRequest = !options.method || options.method === 'GET';
    if (requireAuth && isGetRequest) {
      // Add timestamp to prevent browser/proxy/API Gateway caching
      // Use both _t and _nocache parameters to ensure maximum cache-busting
      const separator = url.includes('?') ? '&' : '?';
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(7);
      url = `${url}${separator}_t=${timestamp}&_nocache=${random}`;
    }

    // #region agent log API_REQUEST_START
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    try {
      const { sendDebugLog } = await import('../utils/debugLogging');
      const requestLogData = { requestId, endpoint, url, method: options.method || 'GET', requireAuth, hasToken: !!this.token, baseUrl: this.baseUrl };
      sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'A', location: 'servicePlansService.ts:request', message: 'API request starting', data: requestLogData, timestamp: Date.now() });
    } catch (_e) {}
    // #endregion

    const includeJsonHeader = !(options.body instanceof FormData);
    const headers = this.createHeaders(options.headers, includeJsonHeader, requireAuth);
    
    // CRITICAL: Add cache-busting headers to prevent stale subscription data
    if (requireAuth && isGetRequest) {
      headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
      headers.set('Pragma', 'no-cache');
      headers.set('Expires', '0');
    }

    const config: RequestInit = { ...options, headers, credentials: 'omit', cache: 'no-store' };

    const startTime = Date.now();
    try {
      // #region agent log API_FETCH_START
      try {
        const { sendDebugLog } = await import('../utils/debugLogging');
        const fetchLogData = { requestId, endpoint, url };
        sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'B', location: 'servicePlansService.ts:request', message: 'Fetch call starting', data: fetchLogData, timestamp: Date.now() });
      } catch (_e) {}
      // #endregion

      const response = await fetch(url, config);
      const fetchDuration = Date.now() - startTime;

      // #region agent log API_RESPONSE_RECEIVED
      try {
        const { sendDebugLog } = await import('../utils/debugLogging');
        const responseLogData = { requestId, endpoint, status: response.status, statusText: response.statusText, ok: response.ok, fetchDuration };
        sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'C', location: 'servicePlansService.ts:request', message: 'Response received', data: responseLogData, timestamp: Date.now() });
      } catch (_e) {}
      // #endregion

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Ensure errorData is always an object to prevent Object.entries errors
        const safeErrorData = errorData && typeof errorData === 'object' ? errorData : {};
        
        const tokenError = createInsufficientTokensError(safeErrorData, response.status);
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        
        // Check if 502 error contains "must sign up" message (expected for new users)
        const errorMessage = safeErrorData.detail?.message || safeErrorData.detail || safeErrorData.message || `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`;

        // #region agent log API_ERROR
        try {
          const { sendDebugLog } = await import('../utils/debugLogging');
          sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'D', location: 'servicePlansService.ts:request', message: 'API request failed', data: { requestId, endpoint, status: response.status, errorMessage, errorData: safeErrorData }, timestamp: Date.now() });
        } catch (_e) {}
        // #endregion

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).errorData = safeErrorData;
        throw error;
      }

      const data = await response.json();
      const totalDuration = Date.now() - startTime;

      // #region agent log API_SUCCESS
      try {
        const { sendDebugLog } = await import('../utils/debugLogging');
        sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'E', location: 'servicePlansService.ts:request', message: 'API request succeeded', data: { requestId, endpoint, totalDuration, hasData: !!data }, timestamp: Date.now() });
      } catch (_e) {}
      // #endregion

      return data;
    } catch (error: any) {
      const totalDuration = Date.now() - startTime;

      // #region agent log API_EXCEPTION
      try {
        const { sendDebugLog } = await import('../utils/debugLogging');
        sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'F', location: 'servicePlansService.ts:request', message: 'API request exception', data: { requestId, endpoint, errorMessage: error?.message, errorStatus: (error as any)?.status, errorName: error?.name, totalDuration, isNetworkError: error?.name === 'TypeError' || error?.message?.includes('fetch') }, timestamp: Date.now() });
      } catch (_e) {}
      // #endregion

      throw error;
    }
  }

  // Get all service plans (public endpoint - no auth required)
  async getServicePlans(): Promise<ServicePlan[]> {
    // #region agent log GET_SERVICE_PLANS_CALLED
    try {
      const { sendDebugLog } = await import('../utils/debugLogging');
      sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'I', location: 'servicePlansService.ts:getServicePlans', message: 'getServicePlans called', data: { baseUrl: this.baseUrl }, timestamp: Date.now() });
    } catch (_e) {}
    // #endregion

    const response = await this.request<{ success: boolean; data: ServicePlan[] }>(
      '/service-plans',
      {},
      false // No authentication required for public plans endpoint
    );

    // #region agent log GET_SERVICE_PLANS_SUCCESS
    try {
      const { sendDebugLog } = await import('../utils/debugLogging');
        sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'J', location: 'servicePlansService.ts:getServicePlans', message: 'getServicePlans succeeded', data: { plansCount: response.data?.length || 0 }, timestamp: Date.now() });
    } catch (_e) {}
    // #endregion

    return response.data || [];
  }

  // Get runtime Stripe pricing-table config (public endpoint)
  async getStripePricingTableConfig(): Promise<StripePricingTableConfig> {
    const response = await this.request<{ success: boolean; data: StripePricingTableConfig }>(
      '/service-plans/stripe-pricing-table',
      {},
      false // Public configuration endpoint
    );
    return response.data;
  }

  // Get a specific service plan by ID
  async getServicePlan(planId: string): Promise<ServicePlan> {
    const response = await this.request<{ success: boolean; data: ServicePlan }>(
      `/service-plans/${planId}`
    );
    return response.data;
  }

  // Get current user's subscription
  async getUserSubscription(): Promise<UserSubscription> {
    // #region agent log GET_USER_SUBSCRIPTION_CALLED
    try {
      const { sendDebugLog } = await import('../utils/debugLogging');
      sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'G', location: 'servicePlansService.ts:getUserSubscription', message: 'getUserSubscription called', data: { baseUrl: this.baseUrl, hasToken: !!this.token }, timestamp: Date.now() });
    } catch (_e) {}
    // #endregion

    // CRITICAL: Add timestamp query parameter to force fresh read from database
    // This bypasses any API Gateway or Lambda response caching
    const timestamp = Date.now();
    const response = await this.request<{ success: boolean; data: UserSubscription }>(
      `/service-plans/user/subscription?fresh=${timestamp}`
    );

    // #region agent log GET_USER_SUBSCRIPTION_SUCCESS
    try {
      const { sendDebugLog } = await import('../utils/debugLogging');
      const successLogData = { hasData: !!response.data, planId: response.data?.currentPlanId, status: response.data?.status, tokensRemaining: response.data?.tokensRemaining, tokenAllowance: response.data?.tokenAllowance };
      sendDebugLog({ sessionId: 'debug-session', runId: 'run12', hypothesisId: 'H', location: 'servicePlansService.ts:getUserSubscription', message: 'getUserSubscription succeeded', data: successLogData, timestamp: Date.now() });
    } catch (_e) {}
    // #endregion

    // CRITICAL: Log the actual response for debugging (use dynamic import to bypass logger if disabled)
    import('../utils/logger').then(({ logger }) => {
      if (logger.isEnabled()) {
        const logData = {
          planId: response.data?.currentPlanId,
          status: response.data?.status,
          tokensRemaining: response.data?.tokensRemaining,
          tokenAllowance: response.data?.tokenAllowance,
        };
        const redactedLogData = redactPII(logData);
        logger.log('DEBUG: getUserSubscription response:', redactedLogData);
      }
    }).catch(() => {
      // Ignore logger import errors
    });

    return response.data;
  }

  // Subscribe to a plan (for new users)
  async subscribeToPlan(
    planId: string,
    billingInterval: 'monthly' | 'annual'
  ): Promise<UserSubscription> {
    const response = await this.request<{ success: boolean; data: UserSubscription }>(
      `/service-plans/${planId}/subscribe`,
      {
        method: 'POST',
        body: JSON.stringify({
          billingInterval,
        }),
      }
    );
    return response.data;
  }

  // Create Stripe Checkout Session (for iframe compatibility)
  async createCheckoutSession(
    planId: string,
    billingInterval: 'monthly' | 'annual'
  ): Promise<{ checkout_url: string; checkout_session_id: string }> {
    const response = await this.request<{ success: boolean; data: { checkout_url: string; checkout_session_id: string } }>(
      `/service-plans/${planId}/checkout`,
      {
        method: 'POST',
        body: JSON.stringify({
          billingInterval,
        }),
      }
    );
    return response.data;
  }

  // Cancel subscription at period end (no proration/refund)
  async cancelSubscription(): Promise<UserSubscription> {
    const response = await this.request<{ success: boolean; data: UserSubscription | { subscription: UserSubscription } }>(
      '/service-plans/user/cancel',
      {
        method: 'POST',
      }
    );

    if (response.data && typeof response.data === 'object' && 'subscription' in response.data) {
      return response.data.subscription;
    }

    return response.data as UserSubscription;
  }

  // Upgrade or change plan
  async upgradePlan(request: SubscriptionUpgradeRequest): Promise<UserSubscription> {
    const response = await this.request<{ success: boolean; data: UserSubscription }>(
      `/service-plans/${request.newPlanId}/upgrade`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    return response.data;
  }

  // Get plan change notifications for current user
  async getPlanChangeNotifications(): Promise<PlanChangeNotification[]> {
    const response = await this.request<{ success: boolean; data: PlanChangeNotification[] }>(
      '/service-plans/notifications'
    );
    return response.data || [];
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean; data: { success: boolean } }>(
      `/service-plans/notifications/${notificationId}/mark-read`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
    return response.data || { success: false };
  }

  // Get usage data for all limits
  async getUserUsage(): Promise<Record<string, { used: number; limit: number | null; type: 'total' | 'monthly'; resetDate?: string }>> {
    try {
      const response = await this.request<{ success: boolean; data?: Record<string, { used: number; limit: number | null; type: 'total' | 'monthly'; resetDate?: string }> }>(
        '/service-plans/user/usage'
      );
      // Ensure we always return an object, never undefined/null
      if (response && typeof response === 'object' && 'data' in response) {
        return response.data || {};
      }
      return {};
    } catch (error: any) {
      // For 403/404/401/502 with "must sign up" message, return empty object (user has no subscription or usage data)
      const is502WithSignUpMessage = error.status === 502 && (
        error.message?.toLowerCase().includes('must sign up') || 
        error.message?.toLowerCase().includes('service plan')
      );
      if (error.status === 403 || error.status === 404 || error.status === 401 || is502WithSignUpMessage) {
        return {};
      }
      // For other errors, still return empty object to prevent crashes
      return {};
    }
  }

  // Get all token costs (public endpoint - no auth required)
  async getTokenCosts(): Promise<Array<{ operation: string; operationKey: string; points: number; description: string }>> {
    const response = await this.request<{ success: boolean; data: Array<{ operation: string; operationKey: string; points: number; description: string }> }>(
      '/service-plans/token-costs',
      {},
      false // No authentication required for public token costs endpoint
    );
    return response.data || [];
  }

  // Update token (called when user logs in)
  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }
}

const servicePlansServiceInstance = new ServicePlansService();

export const servicePlansService = servicePlansServiceInstance;

export default servicePlansServiceInstance;
